import { useRef, useEffect, createElement } from "react"
import { sanitizeHtml } from "./DangerousHTML.utils";

/**
 * A component that sets inner HTML content in the UNSAFEST way possible.
 * This will even execute <script> tags embedded in the HTML string!
 * NEVER run on untrusted sources.
 */

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve()
            return
        }
        const script = document.createElement('script')
        script.src = src
        script.onload = () => resolve()
        script.onerror = reject
        document.head.appendChild(script)
    })
}

export function SuperDangerouslySetHtmlContent(props: {
    html: string,
}) {
    const divRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!props.html || !divRef.current) throw new Error("html prop can't be null")

        // Parse the HTML and extract all script elements
        const container = document.createElement('div')
        container.innerHTML = props.html
        const scripts = Array.from(container.querySelectorAll('script'))
        scripts.forEach(s => s.remove()) // Remove from fragment to avoid double execution

        const externalScripts = scripts.filter(s => s.src)
        const inlineScripts = scripts.filter(s => !s.src)

        // Load all external scripts first, then append the HTML and run inline scripts
        Promise.all(externalScripts.map(s => loadScript(s.src))).then(() => {
            if (!divRef.current) return
            divRef.current.innerHTML = ''
            divRef.current.appendChild(container)
            inlineScripts.forEach(inlineScript => {
                const script = document.createElement('script')
                script.textContent = inlineScript.textContent
                divRef.current!.appendChild(script)
            })
        })
    }, [props.html, divRef])

    return createElement('div', { ref: divRef })
}

/**
 * Renders HTML content from untrusted sources (external WMS servers) after
 * sanitising it. Scripts and event handlers are stripped.
 */
export function SafelySetHtmlContent(props: { html: string }) {
    return createElement('div', {
        dangerouslySetInnerHTML: { __html: sanitizeHtml(props.html) },
    });
}